import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { setCookie } from "@litres/utils";
import type {
  IReduxRequestNode,
  SIDPayload,
  IResponseRedirect,
  IStandardGUJRequestParams,
} from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";

type TRequestActualizedUserSidCat2Node = IReduxRequestNode<null, SIDPayload>;
type TCheckPassCorrectCSRFNode = IReduxRequestNode<
  ICheckPassRequestCSRF,
  ICheckPassResponseCSRF
>;
type TOtpinLoginCSRFNode = IReduxRequestNode<
  IOtpinLoginRequestCSRF,
  IOtpinLoginResponseCSRF
>;
type TLoginCSRFNode = IReduxRequestNode<ILoginParamsCSRF, ILoginReponseCSRF>;
type TAttachSocnetCSRFNode = IReduxRequestNode<
  IAttachSocnetRequestCSRF,
  ISocnetResponseCSRF
>;
type TDetachSocnetCSRFNode = IReduxRequestNode<
  IDetachSocnetRequestCSRF,
  ISocnetResponseCSRF
>;
type TSocnetCSRFNode = IReduxRequestNode<
  ISocnetRequestCSRF,
  ISocnetResponseCSRF
>;
type TCheckSocnetCSRFNode = IReduxRequestNode<
  ICheckSocnetRequestCSRF,
  ICheckSocnetResponseCSRF
>;

interface IUserAuthorizationState {
  actions: {
    requestActualizedUserSidCat2: TRequestActualizedUserSidCat2Node;
    checkPassCorrectCSRF: TCheckPassCorrectCSRFNode;
    otpinLoginCSRF: TOtpinLoginCSRFNode;
    loginCSRF: TLoginCSRFNode;
    attachSocnetCSRF: TAttachSocnetCSRFNode;
    socnetCSRF: TSocnetCSRFNode;
    checkSocnetCSRF: TCheckSocnetCSRFNode;
  };
}

const initialState: IUserAuthorizationState = {
  actions: {
    requestActualizedUserSidCat2: {},
    checkPassCorrectCSRF: {},
    otpinLoginCSRF: {},
    loginCSRF: {},
    attachSocnetCSRF: {},
    socnetCSRF: {},
    checkSocnetCSRF: {},
  },
};

export type IAuthHeaders = Record<string, string>;

interface ILoginParamsCSRF {
  login: string;
  pwd: string;
  tcountry?: string;
  ref_url?: string;
  onSuccess?: (success: TLoginCSRFNode) => void;
  onError?: (error: TLoginCSRFNode) => void;
}

type ILoginRequestCSRF = ILoginParamsCSRF & {
  pre_action: "login";
  gu_ajax: boolean;
  remember_me: string;
  can_have_short_sessions: number;
  redir_off: number;
};

interface ILoginReponseCSRF {
  redirect?: IResponseRedirect;
}

export const requestActualizedUserSidCat2 = createAsyncThunk(
  "user/authorization/requestActualizedUserSidCat2",
  async (params?: {
    onSuccess?: (success: TRequestActualizedUserSidCat2Node) => void;
    onError?: (error: TRequestActualizedUserSidCat2Node) => void;
  }) => {
    const response = new Request.Cat2<null, SIDPayload>({
      url: "/",
      provider: {
        id: "w_actualize_sid",
        func: "w_actualize_sid",
      },
      request: {
        method: "POST",
      },
      onSuccess: params?.onSuccess,
      onError: params?.onError,
    });

    return await response.fetch();
  }
);

export const loginCSRF = createAsyncThunk(
  "user/authorization/loginCSRF",
  async (params: {
    data: ILoginParamsCSRF;
    onSuccess?: (success: TLoginCSRFNode) => void;
    onError?: (error: TLoginCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<ILoginRequestCSRF, ILoginReponseCSRF>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "login",
      },
      request: {
        method: "POST",
        data: {
          pre_action: "login",
          gu_ajax: true,
          remember_me: "on",
          can_have_short_sessions: 1,
          redir_off: 1,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export interface ICheckMail {
  checkmail: number;
}

interface ICheckPassParamsCSRF {
  login2check: string;
  pass2check: string;
  tcountry?: string;
}

interface ICheckPassResponseCSRF {
  actions_output: {
    message: number;
  };
  status: string;
}

type ICheckPassRequestCSRF = IStandardGUJRequestParams &
  ICheckPassParamsCSRF &
  ICheckMail;

export const checkPassCorrectCSRF = createAsyncThunk(
  "user/authorization/checkPassCorrectCSRF",
  async (params: {
    data: ICheckPassParamsCSRF;
    onSuccess?: (success: TCheckPassCorrectCSRFNode) => void;
    onError?: (error: TCheckPassCorrectCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      ICheckPassRequestCSRF,
      ICheckPassResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "checkPassCorrect",
      },
      request: {
        method: "POST",
        data: {
          action: "ajax_login_pass_correct",
          gu_ajax: true,
          checkmail: 1,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IOtpinLoginParamsCSRF {
  otpin: string;
}

type IOtpinLoginRequestCSRF = IOtpinLoginParamsCSRF & IStandardGUJRequestParams;

interface IOtpinLoginResponseCSRF {
  actions_output: {
    message: number;
  };
  status: string;
  error_msg?: string;
}

export const otpinLoginCSRF = createAsyncThunk(
  "user/authorization/otpinLoginCSRF",
  async (params: {
    data: IOtpinLoginParamsCSRF;
    onSuccess?: (success: TOtpinLoginCSRFNode) => void;
    onError?: (error: TOtpinLoginCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IOtpinLoginRequestCSRF,
      IOtpinLoginResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "otpinLoginCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "otpin_login",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface ISocnetParamsCSRF {
  socnet: string;
}

interface ISocnetResponseCSRF {
  status: string;
  redirect?: IResponseRedirect;
}

type IAttachSocnetRequestCSRF = ISocnetParamsCSRF & IStandardGUJRequestParams;
type IDetachSocnetRequestCSRF = ISocnetParamsCSRF & IStandardGUJRequestParams;

export const attachSocnetCSRF = createAsyncThunk(
  "user/authorization/attachSocnetCSRF",
  async (params: {
    data: ISocnetParamsCSRF;
    onSuccess?: (success: TAttachSocnetCSRFNode) => void;
    onError?: (error: TAttachSocnetCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IAttachSocnetRequestCSRF,
      ISocnetResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "attachSocnetCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "attach_socnet",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const detachSocnetCSRF = createAsyncThunk(
  "user/authorization/detachSocnetCSRF",
  async (params: {
    data: ISocnetParamsCSRF;
    onSuccess?: (success: TDetachSocnetCSRFNode) => void;
    onError?: (error: TDetachSocnetCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IDetachSocnetRequestCSRF,
      ISocnetResponseCSRF
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "detachSocnetCSRF",
      },
      request: {
        method: "POST",
        data: {
          action: "detach_socnet",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

type ISocnetRequestCSRF = ISocnetParamsCSRF & {
  pre_action: "socnet";
  gu_ajax: boolean;
};

export const socnetCSRF = createAsyncThunk(
  "user/authorization/socnetCSRF",
  async (params: {
    data: ISocnetParamsCSRF;
    onSuccess?: (success: TSocnetCSRFNode) => void;
    onError?: (error: TSocnetCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<ISocnetRequestCSRF, ISocnetResponseCSRF>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "socnetCSRF",
      },
      request: {
        method: "POST",
        data: {
          pre_action: "socnet",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface ICheckSocnetParamsCSRF {
  action?: "socnet_response";
  access_token?: string;
  code?: string;
  oauth_token?: string;
  oauth_verifier?: string;
}

interface ICheckSocnetResponseCSRF {
  status: string;
  service: string;
  url?: string;
}

type ICheckSocnetRequestCSRF = ICheckSocnetParamsCSRF & {
  gu_ajax: boolean;
};

export const checkSocnetCSRF = createAsyncThunk(
  "user/authorization/checkSocnetCSRF",
  async (params: {
    data: ICheckSocnetParamsCSRF;
    onSuccess?: (success: TCheckSocnetCSRFNode) => void;
    onError?: (error: TCheckSocnetCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      ICheckSocnetRequestCSRF,
      ICheckSocnetResponseCSRF
    >({
      url: "/pages/check_socnet/",
      provider: {
        id: "checkSocnetCSRF",
      },
      request: {
        method: "POST",
        data: {
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const authorizationSlice = createSlice({
  name: "user/authorization",
  initialState,
  reducers: {
    // Нет сеттеров
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "user.authorization");

    // Загрузка
    builder.addCase(loginCSRF.pending, (state: IUserAuthorizationState) => {
      state.actions.loginCSRF.isLoading = true;
      state.actions.loginCSRF.error = undefined;
    });
    // Ответ
    builder.addCase(
      loginCSRF.fulfilled,
      (
        state: IUserAuthorizationState,
        action: PayloadAction<TLoginCSRFNode>
      ) => {
        state.actions.loginCSRF = action.payload;
        state.actions.loginCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      checkPassCorrectCSRF.pending,
      (state: IUserAuthorizationState) => {
        state.actions.checkPassCorrectCSRF.isLoading = true;
        state.actions.checkPassCorrectCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      checkPassCorrectCSRF.fulfilled,
      (
        state: IUserAuthorizationState,
        action: PayloadAction<TCheckPassCorrectCSRFNode>
      ) => {
        state.actions.checkPassCorrectCSRF = action.payload;
        state.actions.checkPassCorrectCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      otpinLoginCSRF.pending,
      (state: IUserAuthorizationState) => {
        state.actions.otpinLoginCSRF.isLoading = true;
        state.actions.otpinLoginCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      otpinLoginCSRF.fulfilled,
      (
        state: IUserAuthorizationState,
        action: PayloadAction<TOtpinLoginCSRFNode>
      ) => {
        state.actions.otpinLoginCSRF = action.payload;
        state.actions.otpinLoginCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      attachSocnetCSRF.pending,
      (state: IUserAuthorizationState) => {
        state.actions.attachSocnetCSRF.isLoading = true;
        state.actions.attachSocnetCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      attachSocnetCSRF.fulfilled,
      (
        state: IUserAuthorizationState,
        action: PayloadAction<TAttachSocnetCSRFNode>
      ) => {
        state.actions.attachSocnetCSRF = action.payload;
        state.actions.attachSocnetCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(socnetCSRF.pending, (state: IUserAuthorizationState) => {
      state.actions.socnetCSRF.isLoading = true;
      state.actions.socnetCSRF.error = undefined;
    });
    // Ответ
    builder.addCase(
      socnetCSRF.fulfilled,
      (
        state: IUserAuthorizationState,
        action: PayloadAction<TSocnetCSRFNode>
      ) => {
        state.actions.socnetCSRF = action.payload;
        state.actions.socnetCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      checkSocnetCSRF.pending,
      (state: IUserAuthorizationState) => {
        state.actions.checkSocnetCSRF.isLoading = true;
        state.actions.checkSocnetCSRF.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      checkSocnetCSRF.fulfilled,
      (
        state: IUserAuthorizationState,
        action: PayloadAction<TCheckSocnetCSRFNode>
      ) => {
        state.actions.checkSocnetCSRF = action.payload;
        state.actions.checkSocnetCSRF.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(
      requestActualizedUserSidCat2.pending,
      (state: IUserAuthorizationState) => {
        state.actions.requestActualizedUserSidCat2.isLoading = true;
        state.actions.requestActualizedUserSidCat2.error = undefined;
      }
    );
    // Ответ
    builder.addCase(
      requestActualizedUserSidCat2.fulfilled,
      (
        state: IUserAuthorizationState,
        action: PayloadAction<TRequestActualizedUserSidCat2Node>
      ) => {
        state.actions.requestActualizedUserSidCat2 = action.payload;
        state.actions.requestActualizedUserSidCat2.isLoading = false;

        if (action.payload.response?.sid) {
          setCookie("SID", action.payload.response.sid, {
            "max-age": 3600 * 24 * 30,
          });
        }
      }
    );
  },
});

export default authorizationSlice.reducer;
